import {
  COLLECTION_GROUP_TYPE,
  TRAIN_MODEL_STATUS,
} from "../../../utils/constants";
import {
  getCategories,
  generateCollection,
  generateBlendCollection,
  getCollections,
  getExploreCollections,
  toggleToFavorite,
  getUserFavorites,
  getUserFollowing,
  deleteCollection,
  hideCollection,
  hideCollectionVip,
  getTrainModels,
  getTrainModel,
  createTrainModel,
  updateTrainModel,
  deleteTrainModel,
  fetchTopCreators,
  regenerateCollection,
  regenerateBlendCollection,
  rateModel,
  fetchDefaultGallery,
  selectCoverForTrainModel,
  selectBlendCollectionForTrainModel,
  enableBlendTrainModel,
  updateBlendTrainModel,
  submitTrainModelForReview,
  publishOnFapify,
  getFolders,
  createFolder,
  putCollectionToFolder,
  deleteFolder,
  generateImageCollection,
  regenerateImageCollection,
  editFolder,
  generateAnonymize,
  fetchAnonymizedCollections,
  selectCollectionsInCollectionGroup,
  fetchCollection,
  getCollectionsByModelId,
  fetchUserGallery,
  addUserGalleryImage,
  deleteUserGalleryImage,
} from "./asyncThunks";

const updateCollectionInList = (collections, collectionId, updater) => {
  if (!Array.isArray(collections)) {
    return;
  }

  const collectionIndex = collections.findIndex(
    (collection) => collection._id === collectionId,
  );

  if (collectionIndex === -1) {
    return;
  }

  collections[collectionIndex] = updater(collections[collectionIndex]);
};

const removeCollectionMetadataByName = (collection, type, name) => {
  if (!Array.isArray(collection?.[type])) {
    return collection?.[type];
  }

  return collection[type].filter((item) =>
    typeof item === "string" ? item !== name : item?.name !== name,
  );
};

const hasCollectionMetadataByName = (collection, type, name) => {
  if (!Array.isArray(collection?.[type])) {
    return false;
  }

  return collection[type].some((item) =>
    typeof item === "string" ? item === name : item?.name === name,
  );
};

const addCollectionMetadataByName = (collection, type, name) => {
  const items = Array.isArray(collection?.[type]) ? collection[type] : [];

  if (hasCollectionMetadataByName(collection, type, name)) {
    return items;
  }

  return [...items, { name }];
};

const getGalleryMediaUrl = (item) => {
  if (!item) {
    return null;
  }

  if (typeof item === "string") {
    return item;
  }

  return item.video?.url || item.image?.url || item.url || null;
};

const getGalleryMediaKey = (item) => {
  const url = getGalleryMediaUrl(item);

  return url ? url.split("?")[0] : null;
};

const mergeUniqueGalleryItems = (items) => {
  const mergedItems = [];
  const seenKeys = new Set();

  items.forEach((item) => {
    const key = getGalleryMediaKey(item);

    if (!key || seenKeys.has(key)) {
      return;
    }

    seenKeys.add(key);
    mergedItems.push(item);
  });

  return mergedItems;
};

const getTrainModelFromBlendPayload = (payload) =>
  payload?.data?.trainModel ?? payload?.trainModel ?? payload?.data ?? null;

const createPendingUserGalleryItem = ({
  previewUrl,
  mediaType,
  clientUploadId,
}) => {
  if (!previewUrl) {
    return null;
  }

  return mediaType === "video"
    ? {
        __uploadRequestId: clientUploadId,
        __uploading: true,
        video: { url: previewUrl },
      }
    : {
        __uploadRequestId: clientUploadId,
        __uploading: true,
        image: { url: previewUrl },
      };
};

const getPendingUserGalleryItems = (items = []) =>
  items.filter((item) => item?.__uploading && item?.__uploadRequestId);

const normalizeGalleryPayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.images)) {
    return payload.images;
  }

  if (Array.isArray(payload?.results)) {
    return payload.results;
  }

  return [];
};

const getGalleryPaginationMeta = (payload, data, requestedPage) => {
  const metaSource =
    payload?.data && !Array.isArray(payload.data) ? payload.data : payload;
  const page = Number(metaSource?.page ?? requestedPage ?? 1);
  const perPage = Number(metaSource?.perPage ?? data.length ?? 0);
  const total = Number(metaSource?.total ?? data.length);
  const maxReached =
    metaSource?.maxReached ??
    (perPage > 0 ? data.length < perPage : data.length === 0);

  return {
    page,
    perPage,
    total,
    maxReached: Boolean(maxReached),
  };
};

export const getCategoriesBuilder = (builder) => {
  builder.addCase(getCategories.pending, (state) => {
    state.categories.loading = true;
  });
  builder.addCase(getCategories.fulfilled, (state, { payload }) => {
    state.categories.data = payload;
    state.categories.loading = false;
  });
  builder.addCase(getCategories.rejected, (state) => {
    state.categories.loading = false;
    state.categories.error = true;
  });
};

export const getCollectionsBuilder = (builder) => {
  builder.addCase(getCollections.pending, (state, { meta }) => {
    const preserveData = meta.arg?.preserveData;

    if (!preserveData) {
      state.collections.loading = true;
      state.collections.data = null;
    }
  });
  builder.addCase(getCollections.fulfilled, (state, { payload, meta }) => {
    const { data, page, perPage, maxReached, total } = payload;
    const preserveData = meta.arg?.preserveData;

    if (preserveData && Array.isArray(state.collections.data)) {
      const firstCollectionDate = Date.parse(
        state.collections.data[0]?.createdAt ?? 0,
      );
      const existingIndexById = new Map(
        state.collections.data.map((collection, index) => [
          collection._id,
          index,
        ]),
      );
      const newCollections = [];

      data.forEach((collection) => {
        const existingIndex = existingIndexById.get(collection._id);

        if (existingIndex === undefined) {
          const collectionDate = Date.parse(collection?.createdAt ?? 0);

          if (collectionDate > firstCollectionDate) {
            newCollections.push(collection);
          }
          return;
        }

        if (
          state.collections.data[existingIndex]?.status !== collection.status
        ) {
          state.collections.data[existingIndex] = collection;
        }
      });

      if (newCollections.length) {
        state.collections.data.splice(0, 0, ...newCollections);
      }
    } else {
      state.collections.data = data;
    }

    state.collections.maxReached = maxReached;
    state.collections.perPage = +perPage;
    state.collections.page = +page;
    state.collections.total = +total;
    state.collections.loading = false;
    state.collections.hasDataBeenFetched = true;
  });
  builder.addCase(getCollections.rejected, (state) => {
    state.collections.loading = false;
    state.collections.error = true;
  });
};

export const getUserFavoritesBuilder = (builder) => {
  builder.addCase(getUserFavorites.pending, (state) => {
    state.favoriteCollections.loading = true;
    state.favoriteCollections.data = [];
  });
  builder.addCase(getUserFavorites.fulfilled, (state, { payload }) => {
    const { data, page, perPage, maxReached, total } = payload;

    if (+page !== 1) {
      state.favoriteCollections.data = [
        ...state.favoriteCollections.data,
        ...data,
      ];
    } else {
      state.favoriteCollections.data = data;
    }

    state.favoriteCollections.maxReached = maxReached;
    state.favoriteCollections.perPage = +perPage;
    state.favoriteCollections.page = +page;
    state.exploreCollections.page = +page;
    state.favoriteCollections.total = +total;
    state.favoriteCollections.loading = false;
    state.favoriteCollections.hasDataBeenFetched = true;
  });
  builder.addCase(getUserFavorites.rejected, (state) => {
    state.favoriteCollections.loading = false;
    state.favoriteCollections.error = true;
  });
};

export const getUserFollowingBuilder = (builder) => {
  builder.addCase(getUserFollowing.pending, (state) => {
    state.followingCollections.loading = true;
    state.followingCollections.data = [];
  });
  builder.addCase(getUserFollowing.fulfilled, (state, { payload }) => {
    const { data, page, perPage, maxReached, total } = payload;

    if (+page !== 1) {
      state.followingCollections.data = [
        ...state.followingCollections.data,
        ...data,
      ];
    } else {
      state.followingCollections.data = data;
    }

    state.followingCollections.maxReached = maxReached;
    state.followingCollections.perPage = +perPage;
    state.followingCollections.page = +page;
    state.exploreCollections.page = +page;
    state.followingCollections.total = +total;
    state.followingCollections.loading = false;
    state.followingCollections.hasDataBeenFetched = true;
  });
  builder.addCase(getUserFollowing.rejected, (state) => {
    state.followingCollections.loading = false;
    state.followingCollections.error = true;
  });
};

export const getExploreCollectionsBuilder = (builder) => {
  builder.addCase(getExploreCollections.pending, (state, { meta }) => {
    state.exploreCollections.loading = true;
    state.exploreCollections.data = [];
    state.exploreCollections.page = meta.arg.page;
  });

  builder.addCase(getExploreCollections.fulfilled, (state, { payload }) => {
    const { data, page, perPage, maxReached, total, categories } = payload;

    if (!state.categories.data || !state.categories.data.length) {
      state.categories.data = categories;
    }
    state.exploreCollections.data = data;

    state.exploreCollections.maxReached = maxReached;
    state.exploreCollections.perPage = +perPage;
    state.exploreCollections.page = +page;
    state.exploreCollections.total = +total;
    state.exploreCollections.loading = false;
    state.exploreCollections.hasDataBeenFetched = true;
  });
  builder.addCase(getExploreCollections.rejected, (state) => {
    state.exploreCollections.loading = false;
    state.exploreCollections.error = true;
  });
};

export const generateCollectionBuilder = (builder) => {
  builder.addCase(generateCollection.pending, (state) => {
    state.generateCollection.loading = true;
  });
  builder.addCase(generateCollection.fulfilled, (state, { payload, meta }) => {
    const { collection } = payload.data;

    if (collection) {
      state.collections.page = 1;
      if (state.collections.data) {
        state.collections.data = [collection, ...state.collections.data];
      } else {
        state.collections.data = [collection];
      }

      const trainModelArg = meta?.arg?.trainModel;
      if (trainModelArg && state.selectedTrainModel.data) {
        const existing = state.selectedTrainModel.data.collections || [];
        state.selectedTrainModel.data.collections = [...existing, collection];
      }
    }

    state.generateCollection.loading = false;
  });
  builder.addCase(generateCollection.rejected, (state) => {
    state.generateCollection.loading = false;
    state.generateCollection.error = true;
  });
};

export const generateBlendCollectionBuilder = (builder) => {
  builder.addCase(generateBlendCollection.pending, (state) => {
    state.generateCollection.loading = true;
  });
  builder.addCase(
    generateBlendCollection.fulfilled,
    (state, { payload, meta }) => {
      const { collection } = payload.data;

      if (collection) {
        state.collections.page = 1;
        if (state.collections.data) {
          state.collections.data = [collection, ...state.collections.data];
        } else {
          state.collections.data = [collection];
        }

        const trainModelArg = meta?.arg?.trainModel;
        if (trainModelArg && state.selectedTrainModel.data) {
          const existing =
            state.selectedTrainModel.data.blendCollections || [];
          state.selectedTrainModel.data.blendCollections = [
            ...existing,
            collection,
          ];
        }
      }

      state.generateCollection.loading = false;
    },
  );
  builder.addCase(generateBlendCollection.rejected, (state) => {
    state.generateCollection.loading = false;
    state.generateCollection.error = true;
  });
};

export const generateImageCollectionBuilder = (builder) => {
  builder.addCase(generateImageCollection.pending, (state) => {
    state.generateImageCollectionPending = true;
  });
  builder.addCase(
    generateImageCollection.fulfilled,
    (state, { payload, meta }) => {
      const { collection } = payload.data;

      if (collection) {
        state.collections.page = 1;
        if (state.collections.data) {
          state.collections.data = [collection, ...state.collections.data];
        } else {
          state.collections.data = [collection];
        }

        const trainModelArg =
          meta?.arg instanceof FormData ? meta.arg.get("trainModel") : null;
        if (trainModelArg && state.selectedTrainModel.data) {
          const existing = state.selectedTrainModel.data.collections || [];
          state.selectedTrainModel.data.collections = [...existing, collection];
        }

        const modelIdArg =
          meta?.arg instanceof FormData ? meta.arg.get("modelId") : null;
        if (
          modelIdArg &&
          state.modelCollections.modelId &&
          String(modelIdArg) === String(state.modelCollections.modelId)
        ) {
          state.modelCollections.data = [
            collection,
            ...(state.modelCollections.data || []),
          ];
          state.modelCollections.total =
            (state.modelCollections.total || 0) + 1;
        }
      }

      state.generateImageCollectionPending = false;
    },
  );
  builder.addCase(generateImageCollection.rejected, (state) => {
    state.generateImageCollectionPending = false;
  });
};

export const regenerateCollectionBuilder = (builder) => {
  builder.addCase(regenerateCollection.pending, (state) => {
    state.regenerateCollectionPending = true;
  });
  builder.addCase(regenerateBlendCollection.pending, (state) => {
    state.regenerateCollectionPending = true;
  });
  builder.addCase(regenerateCollection.fulfilled, (state, { payload }) => {
    const { collection } = payload.data;

    if (collection) {
      state.collections.page = 1;
      if (state.collections.data) {
        state.collections.data = [collection, ...state.collections.data];
      } else {
        state.collections.data = [collection];
      }
    }

    state.regenerateCollectionPending = false;
  });
  builder.addCase(regenerateBlendCollection.fulfilled, (state, { payload }) => {
    const { collection } = payload.data;

    if (collection) {
      state.collections.page = 1;
      if (state.collections.data) {
        state.collections.data = [collection, ...state.collections.data];
      } else {
        state.collections.data = [collection];
      }
    }

    state.regenerateCollectionPending = false;
  });
  builder.addCase(regenerateCollection.rejected, (state) => {
    state.regenerateCollectionPending = false;
  });
  builder.addCase(regenerateBlendCollection.rejected, (state) => {
    state.regenerateCollectionPending = false;
  });
};

export const regenerateImageCollectionBuilder = (builder) => {
  builder.addCase(regenerateImageCollection.pending, (state) => {
    state.regenerateImageCollectionPending = true;
  });
  builder.addCase(regenerateImageCollection.fulfilled, (state, { payload }) => {
    const { collection } = payload.data;

    if (collection) {
      state.collections.page = 1;
      if (state.collections.data) {
        state.collections.data = [collection, ...state.collections.data];
      } else {
        state.collections.data = [collection];
      }
    }

    state.regenerateImageCollectionPending = false;
  });
  builder.addCase(regenerateImageCollection.rejected, (state) => {
    state.regenerateImageCollectionPending = false;
  });
};

export const generateAnonymizeBuilder = (builder) => {
  builder.addCase(generateAnonymize.pending, (state) => {
    state.generateAnonymizedCollectionPending = true;
  });
  builder.addCase(generateAnonymize.fulfilled, (state, { payload }) => {
    state.generateAnonymizedCollectionPending = false;

    state.anonymizedCollections.data = [
      payload,
      ...state.anonymizedCollections.data,
    ];
  });
  builder.addCase(generateAnonymize.rejected, (state) => {
    state.generateAnonymizedCollectionPending = false;
  });
};

export const getAnonymizedCollectionsBuilder = (builder) => {
  builder.addCase(fetchAnonymizedCollections.pending, (state) => {
    state.anonymizedCollections.loading = true;
  });
  builder.addCase(
    fetchAnonymizedCollections.fulfilled,
    (state, { payload }) => {
      state.anonymizedCollections.loading = false;

      state.anonymizedCollections.data = payload;
    },
  );
  builder.addCase(fetchAnonymizedCollections.rejected, (state) => {
    state.anonymizedCollections.loading = false;
  });
};

export const getUserGalleryBuilder = (builder) => {
  builder.addCase(fetchUserGallery.pending, (state, { meta }) => {
    state.userGallery.loading = true;

    if ((meta.arg?.page ?? 1) === 1) {
      state.userGallery.page = 0;
      state.userGallery.maxReached = false;
    }
  });
  builder.addCase(fetchUserGallery.fulfilled, (state, { payload, meta }) => {
    state.userGallery.loading = false;
    const data = normalizeGalleryPayload(payload);
    const { page, perPage, total, maxReached } = getGalleryPaginationMeta(
      payload,
      data,
      meta.arg?.page ?? 1,
    );
    const pendingItems = getPendingUserGalleryItems(state.userGallery.data);

    state.userGallery.data =
      (meta.arg?.page ?? 1) > 1
        ? mergeUniqueGalleryItems([
            ...pendingItems,
            ...state.userGallery.data,
            ...data,
          ])
        : mergeUniqueGalleryItems([...pendingItems, ...data]);
    state.userGallery.page = page;
    state.userGallery.perPage = perPage;
    state.userGallery.total = total;
    state.userGallery.maxReached = maxReached;
  });
  builder.addCase(fetchUserGallery.rejected, (state) => {
    state.userGallery.loading = false;
  });
};

export const addUserGalleryImageBuilder = (builder) => {
  builder.addCase(addUserGalleryImage.pending, (state, { meta }) => {
    state.addUserGalleryImagePending = true;

    const pendingItem = createPendingUserGalleryItem(meta.arg);

    if (!pendingItem) {
      return;
    }

    state.userGallery.data = [pendingItem, ...state.userGallery.data];
  });
  builder.addCase(addUserGalleryImage.fulfilled, (state, { payload }) => {
    state.addUserGalleryImagePending = false;

    const nextUrl = getGalleryMediaUrl(payload?.data);

    if (!nextUrl) {
      return;
    }

    const pendingItem = state.userGallery.data.find(
      (item) => item?.__uploadRequestId === payload.clientUploadId,
    );

    if (!pendingItem) {
      return;
    }

    pendingItem.__uploading = false;

    if (pendingItem.video) {
      pendingItem.video.url = nextUrl;
    } else if (pendingItem.image) {
      pendingItem.image.url = nextUrl;
    } else {
      pendingItem.url = nextUrl;
    }

    state.userGallery.total += 1;
  });
  builder.addCase(addUserGalleryImage.rejected, (state, { meta }) => {
    state.addUserGalleryImagePending = false;
    state.userGallery.data = state.userGallery.data.filter(
      (item) => item?.__uploadRequestId !== meta.arg.clientUploadId,
    );
  });
};

export const deleteUserGalleryImageBuilder = (builder) => {
  builder.addCase(deleteUserGalleryImage.pending, (state, { meta }) => {
    state.deleteUserGalleryImagePending = true;
    const urlsToDelete =
      Array.isArray(meta.arg.urls) && meta.arg.urls.length ? meta.arg.urls : [];
    const keysToDelete = new Set(
      urlsToDelete.map((url) => url?.split("?")[0]).filter(Boolean),
    );
    const nextData = state.userGallery.data.filter(
      (item) => !keysToDelete.has(getGalleryMediaKey(item)),
    );

    state.userGallery.data = nextData;
    state.userGallery.total = Math.max(
      0,
      state.userGallery.total - keysToDelete.size,
    );
  });
  builder.addCase(deleteUserGalleryImage.fulfilled, (state) => {
    state.deleteUserGalleryImagePending = false;
  });
  builder.addCase(deleteUserGalleryImage.rejected, (state) => {
    state.deleteUserGalleryImagePending = false;
  });
};

export const selectCollectionsInCollectionGroupBuilder = (builder) => {
  builder.addCase(selectCollectionsInCollectionGroup.pending, (state) => {
    state.selectCollectionsInCollectionGroupPending = true;
  });
  builder.addCase(
    selectCollectionsInCollectionGroup.fulfilled,
    (state, { payload }) => {
      state.selectCollectionsInCollectionGroupPending = false;
      state.anonymizedCollections.data = [
        ...payload.collections,
        ...state.anonymizedCollections.data.filter(
          (el) =>
            el.type !== COLLECTION_GROUP_TYPE.ANONYMISE_IMAGE ||
            (el.type === COLLECTION_GROUP_TYPE.ANONYMISE_IMAGE &&
              el._id !== payload.group),
        ),
      ];
    },
  );
  builder.addCase(selectCollectionsInCollectionGroup.rejected, (state) => {
    state.selectCollectionsInCollectionGroupPending = false;
  });
};

export const deleteCollectionBuilder = (builder) => {
  builder.addCase(deleteCollection.pending, (state) => {
    state.deleteCollectionPending = true;
  });
  builder.addCase(deleteCollection.fulfilled, (state, { payload }) => {
    const { ids } = payload;

    state.collections.data = state.collections.data.filter(
      (collection) => !ids.includes(collection._id),
    );

    state.deleteCollectionPending = false;
  });
  builder.addCase(deleteCollection.rejected, (state) => {
    state.deleteCollectionPending = false;
  });
};

export const hideCollectionBuilder = (builder) => {
  builder.addCase(hideCollection.pending, (state) => {
    state.hideCollectionPending = true;
  });
  builder.addCase(hideCollection.fulfilled, (state, { payload }) => {
    const { collectionId, hide } = payload;

    updateCollectionInList(
      state.collections.data,
      collectionId,
      (collection) => ({
        ...collection,
        isPublic: !hide,
      }),
    );

    if (state.selectedCollection?._id === collectionId) {
      state.selectedCollection = {
        ...state.selectedCollection,
        isPublic: !hide,
      };
    }

    if (state.collectionDetails.data?._id === collectionId) {
      state.collectionDetails.data = {
        ...state.collectionDetails.data,
        isPublic: !hide,
      };
    }

    state.hideCollectionPending = false;
  });
  builder.addCase(hideCollection.rejected, (state) => {
    state.hideCollectionPending = false;
  });
};

export const hideCollectionVipBuilder = (builder) => {
  builder.addCase(hideCollectionVip.pending, (state) => {
    state.hideCollectionVipPending = true;
  });
  builder.addCase(hideCollectionVip.fulfilled, (state, { payload }) => {
    const { collectionId, hide } = payload;
    const updateCollection = (collection) => ({
      ...collection,
      categories: hide
        ? removeCollectionMetadataByName(
            collection,
            "categories",
            "VIP Creators",
          )
        : addCollectionMetadataByName(collection, "categories", "VIP Creators"),
      tags: hide
        ? removeCollectionMetadataByName(collection, "tags", "VIP")
        : addCollectionMetadataByName(collection, "tags", "VIP"),
    });

    updateCollectionInList(
      state.collections.data,
      collectionId,
      updateCollection,
    );

    if (state.selectedCollection?._id === collectionId) {
      state.selectedCollection = updateCollection(state.selectedCollection);
    }

    if (state.collectionDetails.data?._id === collectionId) {
      state.collectionDetails.data = updateCollection(
        state.collectionDetails.data,
      );
    }

    if (
      state.selectedTrainModel.data?.selectedCollection?._id === collectionId
    ) {
      state.selectedTrainModel.data = {
        ...state.selectedTrainModel.data,
        addToVipCategory: !hide,
        selectedCollection: updateCollection(
          state.selectedTrainModel.data.selectedCollection,
        ),
      };
    }

    state.hideCollectionVipPending = false;
  });
  builder.addCase(hideCollectionVip.rejected, (state) => {
    state.hideCollectionVipPending = false;
  });
};

export const getTrainModelsBuilder = (builder) => {
  builder.addCase(getTrainModels.pending, (state) => {
    state.trainModels.loading = true;
  });
  builder.addCase(getTrainModels.fulfilled, (state, { payload }) => {
    state.trainModels.data = payload.data;
    state.trainModels.page = payload.page;
    state.trainModels.total = payload.total;
    state.trainModels.loading = false;
    state.trainModels.hasDataBeenFetched = true;
  });
  builder.addCase(getTrainModels.rejected, (state) => {
    state.trainModels.loading = false;
    state.trainModels.error = true;
  });
};

export const getTrainModelBuilder = (builder) => {
  builder.addCase(getTrainModel.pending, (state) => {
    state.selectedTrainModel.loading = true;
  });
  builder.addCase(getTrainModel.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.selectedTrainModel.data = data.trainModel;
    state.selectedTrainModel.loading = false;
    state.selectedTrainModel.trainModelId = data.trainModel._id;
  });
  builder.addCase(getTrainModel.rejected, (state) => {
    state.selectedTrainModel.loading = false;
    state.selectedTrainModel.error = true;
  });
};

export const fetchCollectionBuilder = (builder) => {
  builder.addCase(fetchCollection.pending, (state) => {
    state.collectionDetails.loading = true;
  });
  builder.addCase(fetchCollection.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.collectionDetails.data = data;
    state.collectionDetails.loading = false;
  });
  builder.addCase(fetchCollection.rejected, (state) => {
    state.collectionDetails.loading = false;
    state.collectionDetails.data = null;
    state.collectionDetails.error = true;
  });
};

export const getCollectionsByModelIdBuilder = (builder) => {
  builder.addCase(getCollectionsByModelId.pending, (state, { meta }) => {
    state.modelCollections.loading = true;
    state.modelCollections.error = null;
    if (meta?.arg?.modelId) {
      state.modelCollections.modelId = meta.arg.modelId;
    }
  });
  builder.addCase(
    getCollectionsByModelId.fulfilled,
    (state, { payload, meta }) => {
      const { data, page, perPage, maxReached, total } = payload || {};
      state.modelCollections.data = data ?? [];
      state.modelCollections.modelId = meta?.arg?.modelId ?? null;
      state.modelCollections.page = +page || 1;
      state.modelCollections.perPage = +perPage || 10;
      state.modelCollections.maxReached = !!maxReached;
      state.modelCollections.total = +total || 0;
      state.modelCollections.loading = false;
    },
  );
  builder.addCase(getCollectionsByModelId.rejected, (state) => {
    state.modelCollections.loading = false;
    state.modelCollections.error = true;
  });
};

export const createTrainModelBuilder = (builder) => {
  builder.addCase(createTrainModel.pending, (state) => {
    state.createTrainModel.loading = true;
  });
  builder.addCase(createTrainModel.fulfilled, (state, { payload }) => {
    const trainModel = payload.data;

    if (trainModel) {
      state.trainModels.data = [trainModel, ...state.trainModels.data];
    }

    state.createTrainModel.loading = false;
  });
  builder.addCase(createTrainModel.rejected, (state) => {
    state.createTrainModel.loading = false;
    state.createTrainModel.error = true;
  });
};

export const updateTrainModelBuilder = (builder) => {
  builder.addCase(updateTrainModel.pending, (state) => {
    state.updateTrainModel.loading = true;
  });
  builder.addCase(updateTrainModel.fulfilled, (state, { payload }) => {
    const { trainModel } = payload.data;

    state.trainModels.data = state.trainModels.data.map((model) =>
      model._id === trainModel._id ? trainModel : model,
    );

    if (
      state.selectedTrainModel.data &&
      state.selectedTrainModel.data._id === trainModel._id
    ) {
      state.selectedTrainModel.data = trainModel;
    }

    state.updateTrainModel.loading = false;
  });
  builder.addCase(updateTrainModel.rejected, (state) => {
    state.updateTrainModel.loading = false;
    state.updateTrainModel.error = true;
  });
};

export const deleteTrainModelBuilder = (builder) => {
  builder.addCase(deleteTrainModel.pending, (state) => {
    state.deleteTrainModelPending = true;
  });
  builder.addCase(deleteTrainModel.fulfilled, (state, { payload }) => {
    const { id } = payload;

    state.trainModels.data = state.trainModels.data.filter(
      (trainModel) => trainModel._id !== id,
    );

    state.deleteTrainModelPending = false;
  });
  builder.addCase(deleteTrainModel.rejected, (state) => {
    state.deleteTrainModelPending = false;
  });
};

export const getTopCreatorsBuilder = (builder) => {
  builder.addCase(fetchTopCreators.pending, (state) => {
    state.getTopCreatorsPending = true;
  });
  builder.addCase(fetchTopCreators.fulfilled, (state, { payload }) => {
    const { data } = payload;

    state.topCreators = data;
    state.getTopCreatorsPending = false;
  });
  builder.addCase(fetchTopCreators.rejected, (state) => {
    state.getTopCreatorsPending = false;
  });
};

export const rateModelBuilder = (builder) => {
  builder.addCase(rateModel.pending, (state, { meta }) => {
    const { collectionId, rating } = meta.arg;
    state.collections.data = state.collections.data.map((item) =>
      item._id === collectionId ? { ...item, rating } : item,
    );

    state.rateModelPending = true;
  });
  builder.addCase(rateModel.fulfilled, (state, { payload }) => {
    state.rateModelPending = false;
  });
  builder.addCase(rateModel.rejected, (state) => {
    state.rateModelPending = false;
  });
};

export const getDefaultGalleryBuilder = (builder) => {
  builder.addCase(fetchDefaultGallery.pending, (state) => {
    state.getDefaultGalleryPending = true;
  });
  builder.addCase(fetchDefaultGallery.fulfilled, (state, { payload }) => {
    const data = payload;

    state.gallery.data = data;
    state.getDefaultGalleryPending = false;
  });
  builder.addCase(fetchDefaultGallery.rejected, (state) => {
    state.getDefaultGalleryPending = false;
  });
};

export const selectCoverForTrainModelBuilder = (builder) => {
  builder.addCase(selectCoverForTrainModel.pending, (state) => {
    state.selectCoverForTrainModelPending = true;
  });
  builder.addCase(selectCoverForTrainModel.fulfilled, (state, { payload }) => {
    state.selectCoverForTrainModelPending = false;
  });
  builder.addCase(selectCoverForTrainModel.rejected, (state) => {
    state.selectCoverForTrainModelPending = false;
  });
};

export const selectBlendCollectionForTrainModelBuilder = (builder) => {
  builder.addCase(selectBlendCollectionForTrainModel.pending, (state) => {
    state.selectBlendCollectionForTrainModelPending = true;
  });
  builder.addCase(
    selectBlendCollectionForTrainModel.fulfilled,
    (state, { payload }) => {
      const trainModel = getTrainModelFromBlendPayload(payload);

      if (trainModel) {
        state.selectedTrainModel.data = trainModel;
        state.selectedTrainModel.trainModelId = trainModel._id;

        if (Array.isArray(state.trainModels.data)) {
          state.trainModels.data = state.trainModels.data.map((model) =>
            model._id === trainModel._id ? trainModel : model,
          );
        }
      }

      state.selectBlendCollectionForTrainModelPending = false;
    },
  );
  builder.addCase(selectBlendCollectionForTrainModel.rejected, (state) => {
    state.selectBlendCollectionForTrainModelPending = false;
  });
};

export const enableBlendTrainModelBuilder = (builder) => {
  builder.addCase(enableBlendTrainModel.pending, (state) => {
    state.enableBlendTrainModelPending = true;
  });
  builder.addCase(enableBlendTrainModel.fulfilled, (state, { payload }) => {
    const trainModel = getTrainModelFromBlendPayload(payload);

    if (trainModel) {
      state.selectedTrainModel.data = trainModel;
      state.selectedTrainModel.trainModelId = trainModel._id;
    } else if (state.selectedTrainModel.data) {
      state.selectedTrainModel.data = {
        ...state.selectedTrainModel.data,
        isBlendEnabled: true,
      };
    }

    if (
      Array.isArray(state.trainModels.data) &&
      (trainModel?._id || state.selectedTrainModel.data?._id)
    ) {
      state.trainModels.data = state.trainModels.data.map((model) =>
        model._id === (trainModel?._id || state.selectedTrainModel.data?._id)
          ? trainModel || { ...model, isEnabledBlend: true }
          : model,
      );
    }

    state.enableBlendTrainModelPending = false;
  });
  builder.addCase(enableBlendTrainModel.rejected, (state) => {
    state.enableBlendTrainModelPending = false;
  });
};

export const updateBlendTrainModelBuilder = (builder) => {
  builder.addCase(updateBlendTrainModel.pending, (state) => {
    state.updateBlendTrainModelPending = true;
  });
  builder.addCase(updateBlendTrainModel.fulfilled, (state, { payload }) => {
    const trainModel = getTrainModelFromBlendPayload(payload);

    if (trainModel) {
      state.selectedTrainModel.data = trainModel;
      state.selectedTrainModel.trainModelId = trainModel._id;

      if (Array.isArray(state.trainModels.data)) {
        state.trainModels.data = state.trainModels.data.map((model) =>
          model._id === trainModel._id ? trainModel : model,
        );
      }
    }

    state.updateBlendTrainModelPending = false;
  });
  builder.addCase(updateBlendTrainModel.rejected, (state) => {
    state.updateBlendTrainModelPending = false;
  });
};

export const submitTrainModelForReviewBuilder = (builder) => {
  builder.addCase(submitTrainModelForReview.pending, (state) => {
    state.submitTrainModelForReviewPending = true;
  });
  builder.addCase(submitTrainModelForReview.fulfilled, (state, { payload }) => {
    state.selectedTrainModel.data.status =
      TRAIN_MODEL_STATUS.SUBMITTED_FOR_REVIEW;
    state.submitTrainModelForReviewPending = false;
  });
  builder.addCase(submitTrainModelForReview.rejected, (state) => {
    state.submitTrainModelForReviewPending = false;
  });
};

export const publishOnFapifyBuilder = (builder) => {
  builder.addCase(publishOnFapify.pending, (state) => {
    state.publishOnFapifyPending = true;
  });
  builder.addCase(publishOnFapify.fulfilled, (state) => {
    state.publishOnFapifyPending = false;
  });
  builder.addCase(publishOnFapify.rejected, (state) => {
    state.publishOnFapifyPending = false;
  });
};

export const getCollectionFoldersBuilder = (builder) => {
  builder.addCase(getFolders.pending, (state) => {
    state.collectionFolders.loading = true;
  });
  builder.addCase(getFolders.fulfilled, (state, { payload }) => {
    state.collectionFolders.data = payload;
    state.collectionFolders.loading = false;
  });
  builder.addCase(getFolders.rejected, (state) => {
    state.collectionFolders.loading = false;
  });
};

export const createCollectionFolderBuilder = (builder) => {
  builder.addCase(createFolder.pending, (state) => {
    state.createFolderPending = true;
  });
  builder.addCase(createFolder.fulfilled, (state, { payload }) => {
    state.collectionFolders.data = [...state.collectionFolders.data, payload];
    state.createFolderPending = false;
  });
  builder.addCase(createFolder.rejected, (state) => {
    state.createFolderPending = false;
  });
};

export const deleteCollectionFolderBuilder = (builder) => {
  builder.addCase(deleteFolder.pending, (state) => {
    state.deleteFolderPending = true;
  });
  builder.addCase(deleteFolder.fulfilled, (state, { payload }) => {
    state.collectionFolders.data = state.collectionFolders.data.filter(
      (el) => el.name !== payload.name,
    );
    state.deleteFolderPending = false;
  });
  builder.addCase(deleteFolder.rejected, (state) => {
    state.deleteFolderPending = false;
  });
};

export const editCollectionFolderBuilder = (builder) => {
  builder.addCase(editFolder.pending, (state) => {
    state.editFolderPending = true;
  });

  builder.addCase(editFolder.fulfilled, (state, { payload }) => {
    state.editFolderPending = false;

    if (
      state.collectionFolders.data &&
      Array.isArray(state.collectionFolders.data)
    ) {
      const folderIndex = state.collectionFolders.data.findIndex(
        (folder) => folder._id === payload._id,
      );

      if (folderIndex !== -1) {
        state.collectionFolders.data[folderIndex] = payload;
      }
    }
  });

  builder.addCase(editFolder.rejected, (state) => {
    state.editFolderPending = false;
  });
};

export const putCollectionToFolderBuilder = (builder) => {
  builder.addCase(putCollectionToFolder.pending, (state) => {
    state.putCollectionToFolderPending = true;
  });

  builder.addCase(
    putCollectionToFolder.fulfilled,
    (state, { payload, meta }) => {
      state.putCollectionToFolderPending = false;

      if (state.collections.data && Array.isArray(state.collections.data)) {
        const collectionIndex = state.collections.data.findIndex(
          (c) => c._id === payload._id,
        );

        if (collectionIndex !== -1) {
          const currentFolderId = meta.arg.currentFolderId;

          if (
            currentFolderId &&
            currentFolderId !== "ALL" &&
            currentFolderId !== "FAVORITES" &&
            !payload.folders.includes(currentFolderId)
          ) {
            state.collections.data.splice(collectionIndex, 1);
            state.collections.total = Math.max(0, state.collections.total - 1);
          } else {
            state.collections.data[collectionIndex] = payload;
          }
        }
      }
    },
  );

  builder.addCase(putCollectionToFolder.rejected, (state) => {
    state.putCollectionToFolderPending = false;
  });
};

export const toggleToFavoriteBuilder = (builder) => {
  builder.addCase(toggleToFavorite.pending, (state) => {
    state.toggleToFavoritePending = true;
  });

  builder.addCase(toggleToFavorite.fulfilled, (state, { payload, meta }) => {
    state.toggleToFavoritePending = false;

    const collectionId = payload;
    const currentFolderId = meta.arg.currentFolderId;

    if (state.collections.data && Array.isArray(state.collections.data)) {
      const existingIndex = state.collections.data.findIndex(
        (collection) => collection._id === collectionId,
      );

      if (currentFolderId === "FAVORITES") {
        if (existingIndex !== -1) {
          state.collections.data.splice(existingIndex, 1);
          state.collections.total = Math.max(0, state.collections.total - 1);
        } else {
          state.collections.data[existingIndex] = payload;
        }
      }
    }
  });

  builder.addCase(toggleToFavorite.rejected, (state) => {
    state.toggleToFavoritePending = false;
  });
};
